import {
  Box, Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import CustomHeader from '../../components/General/CustomHeader';
import Page from '../../components/Layout/Page';
import apiClient from '../../requests/api/apiClient';
import {
  AccountSchema, FarmSchema,
} from '../../requests/api/apiTypes';
import { ReduxState } from '../../types';
import DevicesTableContent from './components/DevicesTableContent';
import useTableStyles from '../ClientDetail/Layout/styles';

const DevicesListView = () => {
  const classes = useTableStyles();
  const history = useHistory();
  const location = useLocation();
  const { selectedAccount } = useSelector((state: ReduxState) => state.userAccount);

  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      accountId: params.get('accountId') || '',
      farmId: params.get('farmId') || '',
      page: parseInt(params.get('page') || '0', 10),
      rowsPerPage: parseInt(params.get('rowsPerPage') || '10', 10),
    };
  };

  const updateQueryParams = (updates: {
    accountId?: string;
    farmId?:string;
    page?: number;
    rowsPerPage?: number }) => {
    const params = new URLSearchParams(location.search);

    if (updates.accountId !== undefined) {
      if (updates.accountId) {
        params.set('accountId', updates.accountId);
      } else {
        params.delete('accountId');
      }
    }

    if (updates.farmId !== undefined) {
      if (updates.farmId) {
        params.set('farmId', updates.farmId);
      } else {
        params.delete('farmId');
      }
    }
    if (updates.page !== undefined) {
      params.set('page', String(updates.page));
    }
    if (updates.rowsPerPage !== undefined) {
      params.set('rowsPerPage', String(updates.rowsPerPage));
    }

    history.replace({ search: params.toString() });
  };

  const initialParams = getQueryParams();

  const [selectedAccountId, setSelectedAccountId] = useState<string>(initialParams.accountId);
  const [selectedFarmId, setSelectedFarmId] = useState<string>(initialParams.farmId);
  const [accounts, setAccounts] = useState<AccountSchema[]>([]);
  const [paginationParameters, setPaginationParameters] = useState({
    page: initialParams.page,
    rowsPerPage: initialParams.rowsPerPage,
  });

  const [farms, setFarms] = useState<FarmSchema[]>([]);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await apiClient.accounts.accountsList({ limit: 100, page: 0 });
        const sortedAccounts = res.data?.accounts?.sort((a, b) => a.name?.localeCompare(b.name || '') || 0);
        if (sortedAccounts) setAccounts(sortedAccounts as unknown as AccountSchema[]);
      } catch (err) {
        console.error('Error fetching accounts', err);
      }
    };
    fetchAccounts();
  }, [selectedAccount?.id]);

  useEffect(() => {
    const fetchFarms = async () => {
      if (!selectedAccountId) {
        setFarms([]);
        setSelectedFarmId('');
        return;
      }
      try {
        const res = await apiClient.farms.accountsDetail(selectedAccountId);
        const sortedFarms = res.data?.allFarms?.sort((a, b) => a.name?.localeCompare(b.name || '') || 0);
        if (sortedFarms) setFarms(sortedFarms as FarmSchema[]);
      } catch (err) {
        console.error('Error fetching farms', err);
      }
    };
    fetchFarms();
  }, [selectedAccountId]);

  return (
    <Page
      className={classes.root}
      title='Equipos'
    >
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} p={2}>
          <CustomHeader title='Equipos' showBackButton={false} />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="account-filter-label">Cuenta</InputLabel>
            <Select
              labelId="account-filter-label"
              value={selectedAccountId}
              label="Cuenta"
              onChange={(e) => {
                const newAccountId = e.target.value;
                setSelectedAccountId(newAccountId);
                setSelectedFarmId('');
                setPaginationParameters((prev) => ({ ...prev, page: 0 }));
                updateQueryParams({ accountId: newAccountId, farmId: '', page: 0 });
              }}
            >
              <MenuItem value="">Todas las cuentas</MenuItem>
              {accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={!selectedAccountId}>
            <InputLabel id="farm-filter-label">Campo</InputLabel>
            <Select
              labelId="farm-filter-label"
              value={selectedFarmId}
              label="Campo"
              onChange={(e) => {
                const newFarmId = e.target.value;
                setSelectedFarmId(newFarmId);
                setPaginationParameters((prev: { page: number; rowsPerPage: number }) => (
                  { ...prev, page: 0 }));
                updateQueryParams({ farmId: newFarmId, page: 0 });
              }}
            >
              <MenuItem value="">Todos los campos</MenuItem>
              {farms.map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>

        <DevicesTableContent
          selectedAccountId={selectedAccountId}
          selectedFarmId={selectedFarmId}
          fixedPaginationParameters={paginationParameters}
        />
      </Container>
    </Page>
  );
};

export default DevicesListView;
